import { zodResolver } from '@hookform/resolvers/zod';
import { MyExamCenterSearchSelection } from '@report/exam-center-report-management';
import {
  useGetMyExamCentersQuery,
  useGetAllExamCoursesQuery,
  useGetQConfigRefCodesByExamCourseQuery,
} from '@report/graphql';
import { AVAILABLE_YEARS_OPTIONS } from '@report/utils';
import {
  Box,
  Button,
  Divider,
  Flex,
  Form,
  FormSearchSelection,
  FormSearchMultipleSelection,
  Text,
  FormSearchMultipleSelectionWithSelectAllOption,
  Grid,
} from '@shared/components';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { EXAM_COURSE_TYPE_OPTIONS, MONTH_OPTIONS } from '../../constants';
import { useExamCourseAnalysisReportSearchParams } from '../../hooks';

import {
  type ExamCourseAnalysisReportFilterFormOutput,
  type ExamCourseAnalysisReportFilterFormInput,
  ExamCourseAnalysisReportFilterSchema,
} from './schema';

export default function ExamCourseAnalysisReportFilterSection() {
  const [
    { startMonth, startYear, endMonth, endYear, examCenter, courseType, examCourse, qConfigRef },
    { changeFilterParams },
  ] = useExamCourseAnalysisReportSearchParams();

  const formContext = useForm<
    ExamCourseAnalysisReportFilterFormInput,
    unknown,
    ExamCourseAnalysisReportFilterFormOutput
  >({
    defaultValues: {
      startMonth: null,
      startYear: null,
      endMonth: null,
      endYear: null,
      examCenter: [],
      courseType: [],
      examCourse: [],
      qConfigRef: [],
    },
    resolver: zodResolver(ExamCourseAnalysisReportFilterSchema),
  });

  const {
    data: examCourseData,
    loading: isLoadingExamCourses,
    refetch: refetchExamCourses,
  } = useGetAllExamCoursesQuery();
  const examCourseOptions = useMemo(
    () =>
      examCourseData?.reportGetAllExamCourses?.examCourses.map((examCourseItem) => ({
        label: examCourseItem.code,
        value: examCourseItem.code,
      })) ?? [],
    [examCourseData?.reportGetAllExamCourses?.examCourses],
  );

  const watchedExamCourse = formContext.watch('examCourse') ?? [];

  const {
    data: qConfigRefData,
    loading: isLoadingQConfigRef,
    refetch: refetchQConfigRef,
  } = useGetQConfigRefCodesByExamCourseQuery({
    variables: {
      getQConfigRefCodesByExamCourseInput: {
        examCourseCodes: watchedExamCourse.some((item) => item.value === 'all')
          ? null
          : watchedExamCourse.map((item) => item.value),
      },
    },
    skip: watchedExamCourse.length === 0,
  });

  const qConfigRefOptions = useMemo(
    () =>
      qConfigRefData?.reportGetQConfigRefCodesByExamCourse.qConfigRefCodes.map(
        (qConfigRefNumber) => ({
          label: qConfigRefNumber.toString(),
          value: qConfigRefNumber.toString(),
        }),
      ) ?? [],
    [qConfigRefData?.reportGetQConfigRefCodesByExamCourse.qConfigRefCodes],
  );

  useEffect(() => {
    const subscription = formContext.watch((_, { name, type }) => {
      // Clear qConfigRef when examCourse changes
      if (name === 'examCourse' && type === 'change') {
        formContext.setValue('qConfigRef', []);
      }
    });
    return () => subscription.unsubscribe();
  }, [formContext]);

  const { refetch: refetchExamCenters } = useGetMyExamCentersQuery({
    fetchPolicy: 'cache-first',
  });

  const getFormValuesFromSearchParams = useCallback(async () => {
    if (
      !startMonth &&
      !startYear &&
      !endMonth &&
      !endYear &&
      examCenter.length === 0 &&
      courseType.length === 0 &&
      examCourse.length === 0 &&
      qConfigRef.length === 0
    ) {
      return {
        startMonth: null,
        startYear: null,
        endMonth: null,
        endYear: null,
        examCenter: [],
        courseType: [],
        examCourse: [],
        qConfigRef: [],
      };
    }
    const selectedStartMonth = MONTH_OPTIONS.find((month) => month.value === startMonth);
    const selectedEndMonth = MONTH_OPTIONS.find((month) => month.value === endMonth);
    const selectedStartYear = AVAILABLE_YEARS_OPTIONS.find((year) => year.value === startYear);
    const selectedEndYear = AVAILABLE_YEARS_OPTIONS.find((year) => year.value === endYear);

    const { data: fetchedExamCenters } = await refetchExamCenters();
    const selectedExamCenters = examCenter.includes('all')
      ? [
          {
            label: `Select All (${fetchedExamCenters.reportGetMyExamCenters.examCenters.length ?? 0})`,
            value: 'all',
          },
        ]
      : fetchedExamCenters.reportGetMyExamCenters.examCenters
          .filter((examCenterItem) => examCenter.includes(examCenterItem.code))
          .map((examCenterItem) => ({
            label: examCenterItem.code,
            value: examCenterItem.code,
          }));

    const selectedCourseTypes = EXAM_COURSE_TYPE_OPTIONS.filter((courseTypeItem) =>
      courseType.includes(courseTypeItem.value),
    );

    const { data: fetchedExamCourses } = await refetchExamCourses();
    const selectedExamCourses = examCourse.includes('all')
      ? [
          {
            label: `Select All (${fetchedExamCourses.reportGetAllExamCourses.examCourses.length ?? 0})`,
            value: 'all',
          },
        ]
      : fetchedExamCourses.reportGetAllExamCourses.examCourses
          .filter((examCourseItem) => examCourse.includes(examCourseItem.code))
          .map((examCourseItem) => ({
            label: examCourseItem.code,
            value: examCourseItem.code,
          }));

    const { data: fetchedQConfigRef } = await refetchQConfigRef();
    const selectedQConfigRef = qConfigRef.includes('all')
      ? [
          {
            label: `Select All (${fetchedQConfigRef.reportGetQConfigRefCodesByExamCourse.qConfigRefCodes.length})`,
            value: 'all',
          },
        ]
      : fetchedQConfigRef.reportGetQConfigRefCodesByExamCourse.qConfigRefCodes
          .filter((qConfigRefItem) => qConfigRef.includes(qConfigRefItem.toString()))
          .map((qConfigRefItem) => ({
            label: qConfigRefItem.toString(),
            value: qConfigRefItem.toString(),
          }));

    return {
      startMonth: selectedStartMonth ?? null,
      startYear: selectedStartYear ?? null,
      endMonth: selectedEndMonth ?? null,
      endYear: selectedEndYear ?? null,
      examCenter: selectedExamCenters,
      courseType: selectedCourseTypes,
      examCourse: selectedExamCourses,
      qConfigRef: selectedQConfigRef,
    };
  }, [
    refetchExamCenters,
    examCenter,
    refetchExamCourses,
    examCourse,
    refetchQConfigRef,
    qConfigRef,
    startMonth,
    endMonth,
    startYear,
    endYear,
    courseType,
  ]);

  useEffect(() => {
    const setFormValues = async () => {
      const formValues = await getFormValuesFromSearchParams();
      formContext.reset(formValues);
    };
    setFormValues();
  }, [formContext, getFormValuesFromSearchParams]);

  const onSubmit = (data: ExamCourseAnalysisReportFilterFormOutput) => {
    const {
      startMonth,
      startYear,
      endMonth,
      endYear,
      examCenter,
      courseType,
      examCourse,
      qConfigRef,
    } = data;

    changeFilterParams({
      startMonth: startMonth.value,
      startYear: startYear.value,
      endMonth: endMonth.value,
      endYear: endYear.value,
      examCenter: examCenter.map((item) => item.value),
      courseType: courseType.map((item) => item.value),
      examCourse: examCourse.map((item) => item.value),
      qConfigRef: qConfigRef.map((item) => item.value),
    });
  };

  const handleReset = () => {
    changeFilterParams({
      startMonth: null,
      startYear: null,
      endMonth: null,
      endYear: null,
      examCenter: [],
      courseType: [],
      examCourse: [],
      qConfigRef: [],
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form
        sx={(theme) => ({
          p: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
        onSubmit={formContext.handleSubmit(onSubmit)}
      >
        <Grid
          sx={{
            gridTemplateColumns: '160px 1fr',
            flexDirection: 'column',
          }}
        >
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Date Filter:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flex: 1,
              alignItems: 'flex-start',
            })}
          >
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(2),
                alignItems: 'flex-start',
                flexBasis: '408px',
              })}
            >
              <Box sx={{ flex: 1 }}>
                <FormSearchSelection
                  name='startMonth'
                  label='Start Month'
                  placeholder='เลือก Month'
                  options={MONTH_OPTIONS}
                />
              </Box>
              <Box sx={(theme) => ({ flex: 1, pt: theme.spacing(6) })}>
                <FormSearchSelection
                  name='startYear'
                  placeholder='เลือก Year'
                  options={AVAILABLE_YEARS_OPTIONS}
                />
              </Box>
            </Flex>
            <Text sx={(theme) => ({ whiteSpace: 'nowrap', pt: theme.spacing(8) })}>to</Text>
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(2),
                alignItems: 'flex-start',
                flexBasis: '408px',
              })}
            >
              <Box sx={{ flex: 1 }}>
                <FormSearchSelection
                  name='endMonth'
                  label='End Month'
                  placeholder='เลือก Month'
                  options={MONTH_OPTIONS}
                />
              </Box>
              <Box sx={(theme) => ({ flex: 1, pt: theme.spacing(6) })}>
                <FormSearchSelection
                  name='endYear'
                  placeholder='เลือก Year'
                  options={AVAILABLE_YEARS_OPTIONS}
                />
              </Box>
            </Flex>
          </Flex>
        </Grid>
        <Grid
          sx={{
            gridTemplateColumns: '160px 1fr',
            flexDirection: 'column',
          }}
        >
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Other Filter:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flex: 1,
              alignItems: 'flex-start',
            })}
          >
            <Box sx={{ flexBasis: '256px' }}>
              <MyExamCenterSearchSelection
                name='examCenter'
                placeholder={
                  (formContext.watch('examCenter') ?? []).length === 0 ? 'เลือก Exam Center' : ''
                }
              />
            </Box>
            <Box sx={{ flexBasis: '256px' }}>
              <FormSearchMultipleSelection
                name='courseType'
                label='Course Type'
                loading={isLoadingExamCourses}
                placeholder={
                  (formContext.watch('courseType') ?? []).length === 0 ? 'เลือก Course Type' : ''
                }
                options={EXAM_COURSE_TYPE_OPTIONS}
              />
            </Box>
            <Box sx={{ flexBasis: '256px' }}>
              <FormSearchMultipleSelectionWithSelectAllOption
                name='examCourse'
                label='Exam Course'
                slotProps={{
                  selectAllOption: {
                    label: `Select All (${examCourseOptions.length})`,
                  },
                }}
                placeholder={
                  (formContext.watch('examCourse') ?? []).length === 0 ? 'เลือก Exam Course' : ''
                }
                options={examCourseOptions}
              />
            </Box>
            <Box sx={{ flexBasis: '256px' }}>
              <FormSearchMultipleSelectionWithSelectAllOption
                name='qConfigRef'
                label='QConfig Ref.'
                disabled={formContext.watch('examCourse')?.length === 0}
                slotProps={{
                  selectAllOption: {
                    label: `Select All (${qConfigRefOptions.length})`,
                  },
                }}
                loading={isLoadingQConfigRef}
                placeholder={
                  (formContext.watch('qConfigRef') ?? []).length === 0 ? 'เลือก QConfig Ref.' : ''
                }
                options={qConfigRefOptions}
              />
            </Box>
          </Flex>
        </Grid>
        <Divider />
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button variant='outlined' color='secondary' onClick={handleReset}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
